import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { ChevronRight, MapPin, Phone, Mail, Clock, CheckCircle, Wrench, Settings, Shield, Zap, BarChart3, Cpu, RefreshCw, Thermometer, FileText } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const services = [
  { icon: Settings, label: 'Spare Parts Enquiries' },
  { icon: FileText, label: 'Product Enquiries' },
  { icon: Shield, label: 'Annual Maintenance Contracts (AMC)' },
  { icon: BarChart3, label: 'Energy Audits' },
  { icon: Wrench, label: 'Compressor Overhauling' },
  { icon: Thermometer, label: 'Evaporator & Condenser Services' },
  { icon: Cpu, label: 'Screw Compressor Requirements' },
  { icon: Zap, label: 'Heat Load Calculations' },
  { icon: RefreshCw, label: 'Chiller Retrofits & Upgrades' },
];

export default function Contact() {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', company: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    document.title = 'Contact Us | Laminate Chillers – Industrial Chiller Manufacturer Hyderabad';
  }, []);

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) { toast.error('Please fill in required fields'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/contact`, formData);
      setSubmitted(true);
      toast.success('Message sent successfully!');
    } catch { toast.error('Failed to send. Please try again.'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="page-transition">
      {/* Header */}
      <section className="py-10 sm:py-14" style={{ background: 'var(--surface-2)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
            <Link to="/" className="hover:text-[var(--brand-navy)]">Home</Link>
            <ChevronRight className="w-3.5 h-3.5" /><span>Contact</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>Contact Us</h1>
          <p className="text-muted-foreground mt-1">Get in touch with our sales, service, and engineering teams</p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-8" style={{ background: 'var(--surface-0)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Head Office */}
            <Card style={{ background: 'var(--surface-1)' }}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(14,138,138,0.1)' }}>
                    <MapPin className="w-4 h-4" style={{ color: 'var(--brand-teal)' }} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm mb-1" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>Head Office</h3>
                    <p className="text-xs text-muted-foreground font-medium" style={{ color: 'var(--brand-navy)' }}>LAMINATE CHILLERS</p>
                    <p className="text-xs text-muted-foreground"># 8 & 9/B, IDA Patancheru</p>
                    <p className="text-xs text-muted-foreground">Behind Veljan Hydra, Patancheru</p>
                    <p className="text-xs text-muted-foreground">Sangareddy District</p>
                    <p className="text-xs text-muted-foreground">Hyderabad – 502319</p>
                    <p className="text-xs text-muted-foreground">Telangana, India</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Sales & Service */}
            <Card style={{ background: 'var(--surface-1)' }}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(14,138,138,0.1)' }}>
                    <Phone className="w-4 h-4" style={{ color: 'var(--brand-teal)' }} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm mb-1" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>Sales & Service</h3>
                    <p className="text-xs text-muted-foreground">+91 90302 23423</p>
                    <p className="text-xs text-muted-foreground">+91 91002 11739</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Email */}
            <Card style={{ background: 'var(--surface-1)' }}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(14,138,138,0.1)' }}>
                    <Mail className="w-4 h-4" style={{ color: 'var(--brand-teal)' }} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm mb-1" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>Email</h3>
                    <p className="text-xs text-muted-foreground">laminatechillers@gmail.com</p>
                    <p className="text-xs text-muted-foreground">Krishna@laminatechillers.com</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Business Hours */}
            <Card style={{ background: 'var(--surface-1)' }}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(14,138,138,0.1)' }}>
                    <Clock className="w-4 h-4" style={{ color: 'var(--brand-teal)' }} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm mb-1" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>Business Hours</h3>
                    <p className="text-xs text-muted-foreground">Monday – Saturday: 9:00 AM – 6:00 PM IST</p>
                    <p className="text-xs text-muted-foreground">Saturday: 9:00 AM – 1:00 PM IST</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Service & Technical Enquiries */}
      <section className="py-8 border-t" style={{ background: 'var(--surface-2)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-xl font-semibold tracking-tight mb-2" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>Service & Technical Enquiries</h2>
          <p className="text-sm text-muted-foreground mb-6">For any of the following requirements, please send us your enquiry:</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {services.map((svc, i) => (
              <Card key={i} style={{ background: 'var(--surface-1)' }}>
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(14,138,138,0.1)' }}>
                    <svc.icon className="w-4 h-4" style={{ color: 'var(--brand-teal)' }} />
                  </div>
                  <span className="text-sm" style={{ color: 'var(--brand-navy)' }}>{svc.label}</span>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form + Map */}
      <section className="py-8" style={{ background: 'var(--surface-0)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Form */}
            <div>
              {submitted ? (
                <div className="text-center py-16">
                  <CheckCircle className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--brand-teal)' }} />
                  <h2 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>Message Sent Successfully</h2>
                  <p className="text-muted-foreground text-sm">Thank you for your enquiry. Our team will respond within 24–48 hours.</p>
                  <Button className="mt-4" variant="outline" onClick={() => { setSubmitted(false); setFormData({ name: '', email: '', phone: '', company: '', subject: '', message: '' }); }}>Send Another Message</Button>
                </div>
              ) : (
                <Card style={{ background: 'var(--surface-1)' }}>
                  <CardContent className="p-6">
                    <h2 className="font-semibold text-base mb-4" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>Send us a message</h2>
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div><Label>Name *</Label><Input data-testid="contact-name" value={formData.name} onChange={(e) => handleChange('name', e.target.value)} required placeholder="Your full name" /></div>
                        <div><Label>Email *</Label><Input type="email" data-testid="contact-email" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} required placeholder="your@email.com" /></div>
                        <div><Label>Phone</Label><Input value={formData.phone} onChange={(e) => handleChange('phone', e.target.value)} placeholder="+91 XXXXX XXXXX" /></div>
                        <div><Label>Company</Label><Input value={formData.company} onChange={(e) => handleChange('company', e.target.value)} placeholder="Company name" /></div>
                      </div>
                      <div><Label>Subject</Label><Input value={formData.subject} onChange={(e) => handleChange('subject', e.target.value)} placeholder="e.g., Spare Parts Enquiry, AMC Request, Product Enquiry" /></div>
                      <div><Label>Message *</Label><Textarea rows={5} data-testid="contact-message" value={formData.message} onChange={(e) => handleChange('message', e.target.value)} required placeholder="Please describe your requirement in detail..." /></div>
                      <Button type="submit" disabled={submitting} className="w-full text-white" style={{ background: 'var(--brand-teal)' }} data-testid="contact-submit">
                        {submitting ? 'Sending...' : 'Send Enquiry'}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Map */}
            <div>
              <Card style={{ background: 'var(--surface-1)' }}>
                <CardContent className="p-0 overflow-hidden rounded-lg">
                  <iframe
                    title="Laminate Chillers Location - Patancheru, Hyderabad"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3804.474!2d78.2647!3d17.5326!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTfCsDMxJzU3LjQiTiA3OMKwMTUnNTMuMCJF!5e0!3m2!1sen!2sin!4v1700000000000"
                    width="100%"
                    height="300"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </CardContent>
              </Card>
              <Card className="mt-4" style={{ background: 'var(--brand-navy)' }}>
                <CardContent className="p-4">
                  <h3 className="font-semibold text-sm text-white mb-2" style={{ fontFamily: 'Space Grotesk' }}>Laminate Chillers</h3>
                  <p className="text-xs text-slate-400"># 8 & 9/B, IDA Patancheru, Behind Veljan Hydra</p>
                  <p className="text-xs text-slate-400">Sangareddy District, Hyderabad – 502319, Telangana, India</p>
                  <div className="flex gap-4 mt-3">
                    <a href="tel:+919030223423" className="text-xs text-[var(--brand-teal-2)] hover:underline">+91 90302 23423</a>
                    <a href="mailto:laminatechillers@gmail.com" className="text-xs text-[var(--brand-teal-2)] hover:underline">laminatechillers@gmail.com</a>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
